import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Music, Upload, Save, Loader2, RefreshCw, Radio, CheckCircle2, AlertTriangle, Trash2, Play } from 'lucide-react';
import PronunciationCard from './PronunciationCard';

const API = process.env.REACT_APP_BACKEND_URL;

const SLOTS = [
  { field: 'intro_url', label: 'Intro Sting', hint: 'Plays at full volume before the read starts.' },
  { field: 'bed_url', label: 'Music Bed', hint: 'Loops quietly underneath the whole narration.' },
  { field: 'outro_url', label: 'Outro Sting', hint: 'Plays after the final line - the sign-off.' },
];

const AudioSlot = ({ slot, url, busy, onPick, onClear }) => {
  const inputRef = useRef(null);
  return (
    <div className="rounded-lg border border-zinc-800 bg-zinc-950/60 p-4" data-testid={`audio-slot-${slot.field}`}>
      <div className="flex items-start justify-between gap-3 mb-2">
        <div>
          <p className="text-[10px] font-black uppercase tracking-[0.25em] text-red-400">{slot.label}</p>
          <p className="text-xs text-zinc-500 leading-snug mt-0.5">{slot.hint}</p>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={busy}
            data-testid={`audio-upload-${slot.field}`}
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded bg-zinc-800 hover:bg-zinc-700 disabled:opacity-50 border border-zinc-700 text-white text-[10px] font-black uppercase tracking-widest"
          >
            {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Upload className="h-3 w-3" />}
            {url ? 'Replace' : 'Upload'}
          </button>
          {url && (
            <button
              type="button"
              onClick={onClear}
              data-testid={`audio-clear-${slot.field}`}
              className="p-1.5 rounded bg-zinc-900 hover:bg-red-900/40 border border-zinc-700 text-zinc-400 hover:text-red-300"
              title="Remove this track"
            >
              <Trash2 className="h-3 w-3" />
            </button>
          )}
        </div>
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="audio/mpeg,audio/mp3,audio/wav,audio/aac,audio/mp4"
        className="hidden"
        onChange={(e) => e.target.files?.[0] && onPick(e.target.files[0])}
      />
      {url ? (
        <audio src={url} controls preload="none" className="w-full mt-1 h-9" data-testid={`audio-preview-${slot.field}`} />
      ) : (
        <p className="text-xs text-zinc-600 mt-1">Nothing uploaded yet.</p>
      )}
    </div>
  );
};

const AudioBrandingTab = ({ token }) => {
  const [form, setForm] = useState(null);
  const [status, setStatus] = useState(null);
  const [voices, setVoices] = useState([]);
  const [previewUrl, setPreviewUrl] = useState('');
  const [previewBusy, setPreviewBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [busyField, setBusyField] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const authHeaders = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const load = useCallback(async () => {
    try {
      const [b, s] = await Promise.all([
        fetch(`${API}/api/admin/audio-branding`, { headers: authHeaders }).then((r) => r.json()),
        fetch(`${API}/api/admin/podcast/status`, { headers: authHeaders }).then((r) => r.json()),
      ]);
      setForm({
        enabled: b.enabled !== false,
        intro_url: b.intro_url || '',
        bed_url: b.bed_url || '',
        outro_url: b.outro_url || '',
        cover_url: b.cover_url || '',
        bed_gain_db: typeof b.bed_gain_db === 'number' ? b.bed_gain_db : -18,
        sting_gain_db: typeof b.sting_gain_db === 'number' ? b.sting_gain_db : 0,
        read_enabled: b.read_enabled !== false,
        intro_read_text: b.intro_read_text || 'A report by Outlaw Media, Melbourne.',
        outro_read_text: b.outro_read_text || 'This has been a report by Outlaw Media, Melbourne.',
        read_voice_id: b.read_voice_id || '',
      });
      setStatus(s);
      fetch(`${API}/api/admin/audio-branding/voices`, { headers: authHeaders })
        .then((r) => (r.ok ? r.json() : { voices: [] }))
        .then((d) => {
          setVoices(d.voices || []);
          setForm((f) => (f && !f.read_voice_id
            ? { ...f, read_voice_id: d.default_read_voice_id || '' }
            : f));
        })
        .catch(() => {});
    } catch (e) {
      setError(e.message);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { load(); }, [load]);

  // Poll while a rebuild is running
  useEffect(() => {
    if (status?.job?.status !== 'running') return undefined;
    const t = setInterval(async () => {
      try {
        const s = await fetch(`${API}/api/admin/podcast/status`, { headers: authHeaders }).then((r) => r.json());
        setStatus(s);
      } catch { /* ignore */ }
    }, 3000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status?.job?.status, token]);

  const upload = async (field, file) => {
    setBusyField(field);
    setError('');
    try {
      const presign = await fetch(`${API}/api/admin/audio-branding/presign`, {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify({ filename: file.name, content_type: file.type || 'audio/mpeg' }),
      });
      const data = await presign.json();
      if (!presign.ok) throw new Error(data.detail || 'Could not get an upload URL.');
      const put = await fetch(data.upload_url, {
        method: 'PUT',
        headers: { 'Content-Type': file.type || 'audio/mpeg' },
        body: file,
      });
      if (!put.ok) throw new Error(`Upload failed (HTTP ${put.status})`);
      setForm((f) => ({ ...f, [field]: data.public_url }));
      setMessage('Uploaded. Hit Save to apply it to new episodes.');
    } catch (e) {
      setError(e.message);
    } finally {
      setBusyField('');
    }
  };

  const previewRead = async (field) => {
    setPreviewBusy(true);
    setError('');
    setPreviewUrl('');
    try {
      const res = await fetch(`${API}/api/admin/audio-branding/preview-read`, {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify({ text: form[field], voice_id: form.read_voice_id || null }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Preview failed.');
      setPreviewUrl(data.audio_url);
    } catch (e) {
      setError(e.message);
    } finally {
      setPreviewBusy(false);
    }
  };

  const save = async () => {
    setSaving(true);
    setError('');
    setMessage('');
    try {
      const res = await fetch(`${API}/api/admin/audio-branding`, {
        method: 'PUT',
        headers: authHeaders,
        body: JSON.stringify({
          ...form,
          intro_url: form.intro_url || null,
          bed_url: form.bed_url || null,
          outro_url: form.outro_url || null,
          cover_url: form.cover_url || null,
          intro_read_text: form.intro_read_text || null,
          outro_read_text: form.outro_read_text || null,
          read_voice_id: form.read_voice_id || null,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Save failed.');
      setMessage('Saved. New episodes use this mix - hit Rebuild to re-score the back catalogue.');
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const rebuild = async (includeNew) => {
    const msg = includeNew
      ? `Narrate every published story that has never been voiced (${status?.never_narrated || 0} of them) and score them with the current music? This DOES spend ElevenLabs credits.`
      : 'Re-mix every already-narrated story with the current music settings? Narration is reused from cache, so this costs no ElevenLabs credits.';
    if (!window.confirm(msg)) return;
    setError('');
    try {
      const res = await fetch(`${API}/api/admin/podcast/rebuild`, {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify({ force: true, include_new: !!includeNew }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Rebuild failed to start.');
      setMessage('Rebuild started - progress updates below.');
      setStatus((s) => ({ ...s, job: { status: 'running', done: 0, total: includeNew ? (s?.published || 0) : (s?.narrated || 0) } }));
    } catch (e) {
      setError(e.message);
    }
  };

  if (!form) {
    return <div className="text-gray-400" data-testid="audio-branding-loading">Loading audio settings…</div>;
  }

  const job = status?.job || {};

  return (
    <div className="space-y-5" data-testid="audio-branding-tab">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-xl font-black text-white tracking-tight flex items-center gap-2">
            <Music className="h-5 w-5 text-red-500" /> Audio &amp; Podcast
          </h2>
          <p className="text-sm text-zinc-400 leading-snug mt-1 max-w-2xl">
            Upload your own news-style sting and bed. Every published story is narrated, scored
            <span className="text-white font-bold"> intro → ducked bed under the read → outro</span>,
            and pushed to each subscriber&apos;s private podcast feed.
          </p>
        </div>
        <button
          type="button"
          onClick={save}
          disabled={saving}
          data-testid="audio-branding-save"
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded bg-red-600 hover:bg-red-500 disabled:opacity-50 text-white text-[11px] font-black uppercase tracking-widest"
        >
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
          Save
        </button>
      </div>

      {message && (
        <div className="flex items-start gap-2 rounded border border-emerald-500/40 bg-emerald-900/20 px-3 py-2 text-xs font-bold text-emerald-200" data-testid="audio-branding-message">
          <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0" /> <span>{message}</span>
        </div>
      )}
      {error && (
        <div className="flex items-start gap-2 rounded border border-red-500/50 bg-red-900/20 px-3 py-2 text-xs font-bold text-red-300" data-testid="audio-branding-error">
          <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" /> <span>{error}</span>
        </div>
      )}

      <label className="flex items-center gap-3 rounded-lg border border-zinc-800 bg-zinc-950/60 px-4 py-3 cursor-pointer">
        <input
          type="checkbox"
          checked={form.enabled}
          onChange={(e) => setForm({ ...form, enabled: e.target.checked })}
          data-testid="audio-branding-enabled"
          className="h-4 w-4 accent-red-600"
        />
        <span className="text-sm text-white font-bold">Music on</span>
        <span className="text-xs text-zinc-500">
          Off = dry narration everywhere (site Listen bar and podcast feed).
        </span>
      </label>

      <div className="grid gap-3 md:grid-cols-3">
        {SLOTS.map((slot) => (
          <AudioSlot
            key={slot.field}
            slot={slot}
            url={form[slot.field]}
            busy={busyField === slot.field}
            onPick={(file) => upload(slot.field, file)}
            onClear={() => setForm({ ...form, [slot.field]: '' })}
          />
        ))}
      </div>

      <div className="grid gap-4 sm:grid-cols-2 rounded-lg border border-zinc-800 bg-zinc-950/60 p-4">
        <div>
          <label className="text-[10px] font-black uppercase tracking-[0.25em] text-zinc-400">
            Bed level under the voice · <span className="text-white">{form.bed_gain_db} dB</span>
          </label>
          <input
            type="range"
            min={-40}
            max={0}
            step={1}
            value={form.bed_gain_db}
            onChange={(e) => setForm({ ...form, bed_gain_db: Number(e.target.value) })}
            data-testid="audio-bed-gain"
            className="w-full mt-2 accent-red-600"
          />
          <p className="text-xs text-zinc-500 mt-1">-18 dB is the classic newsroom murmur. Lower = subtler.</p>
        </div>
        <div>
          <label className="text-[10px] font-black uppercase tracking-[0.25em] text-zinc-400">
            Sting level · <span className="text-white">{form.sting_gain_db} dB</span>
          </label>
          <input
            type="range"
            min={-30}
            max={6}
            step={1}
            value={form.sting_gain_db}
            onChange={(e) => setForm({ ...form, sting_gain_db: Number(e.target.value) })}
            data-testid="audio-sting-gain"
            className="w-full mt-2 accent-red-600"
          />
          <p className="text-xs text-zinc-500 mt-1">Applies to the intro and outro stings.</p>
        </div>
      </div>

      <div className="rounded-lg border border-zinc-800 bg-zinc-950/60 p-4 space-y-4" data-testid="spoken-ident-card">
        <div className="flex flex-wrap items-center gap-3">
          <p className="text-[10px] font-black uppercase tracking-[0.25em] text-red-400">Spoken top-and-tail</p>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={form.read_enabled}
              onChange={(e) => setForm({ ...form, read_enabled: e.target.checked })}
              data-testid="audio-read-enabled"
              className="h-4 w-4 accent-red-600"
            />
            <span className="text-sm text-white font-bold">On</span>
          </label>
          <span className="text-xs text-zinc-500">
            An announcer voice (not Ryan&apos;s) opens and closes every episode.
          </span>
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          {[
            { field: 'intro_read_text', label: 'Opening line' },
            { field: 'outro_read_text', label: 'Sign-off line' },
          ].map(({ field, label }) => (
            <div key={field}>
              <label className="text-[10px] font-black uppercase tracking-[0.25em] text-zinc-400">{label}</label>
              <div className="flex gap-2 mt-1">
                <input
                  type="text"
                  value={form[field]}
                  maxLength={300}
                  onChange={(e) => setForm({ ...form, [field]: e.target.value })}
                  data-testid={`audio-${field.replace(/_/g, '-')}`}
                  className="flex-1 bg-zinc-950 border border-zinc-700 focus:border-red-500 rounded px-3 py-2 text-sm text-white outline-none"
                />
                <button
                  type="button"
                  onClick={() => previewRead(field)}
                  disabled={previewBusy || !form[field]}
                  data-testid={`audio-preview-read-${field === 'intro_read_text' ? 'intro' : 'outro'}`}
                  className="shrink-0 inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded bg-zinc-800 hover:bg-zinc-700 disabled:opacity-50 border border-zinc-700 text-white text-[10px] font-black uppercase tracking-widest"
                >
                  {previewBusy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Play className="h-3 w-3" />}
                  Hear
                </button>
              </div>
            </div>
          ))}
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <label className="text-[10px] font-black uppercase tracking-[0.25em] text-zinc-400">Announcer voice</label>
            <select
              value={form.read_voice_id}
              onChange={(e) => setForm({ ...form, read_voice_id: e.target.value })}
              data-testid="audio-read-voice"
              className="w-full mt-1 bg-zinc-950 border border-zinc-700 focus:border-red-500 rounded px-3 py-2 text-sm text-white outline-none"
            >
              {!voices.length && <option value={form.read_voice_id}>Loading voices…</option>}
              {voices.map((v) => (
                <option key={v.voice_id} value={v.voice_id}>
                  {v.name}{v.category ? ` · ${v.category}` : ''}
                </option>
              ))}
            </select>
            <p className="text-xs text-zinc-500 mt-1">
              Pick any voice on your ElevenLabs account - keep it different to Ryan&apos;s clone so the ident stands apart.
            </p>
          </div>
          {previewUrl && (
            <div className="flex flex-col justify-end">
              <label className="text-[10px] font-black uppercase tracking-[0.25em] text-zinc-400">Preview</label>
              <audio src={previewUrl} controls autoPlay className="w-full mt-1 h-9" data-testid="audio-read-preview-player" />
            </div>
          )}
        </div>

        {status?.voice_disclaimer && (
          <p className="text-[11px] text-zinc-500 leading-snug border-t border-zinc-800 pt-3" data-testid="voice-clone-note">
            <span className="text-zinc-300 font-bold">Note on the read: </span>{status.voice_disclaimer}
            {' '}This note is shown to readers on the Listen bar, their account page and in the podcast feed description.
          </p>
        )}
      </div>

      <PronunciationCard token={token} />

      <div className="rounded-lg border border-zinc-800 bg-zinc-950/60 p-4" data-testid="podcast-status-card">
        <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-sm">
          <span className="inline-flex items-center gap-1.5 text-[10px] font-black uppercase tracking-[0.25em] text-red-400">
            <Radio className="h-3.5 w-3.5" /> Podcast feed
          </span>
          <span className="text-zinc-300">{status?.with_audio || 0}/{status?.published || 0} episodes rendered</span>
          <span className="text-zinc-500">{status?.with_music || 0} scored with music</span>
          <span className="text-zinc-500">{status?.feed_subscribers || 0} private feeds issued</span>
          <div className="ml-auto flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => rebuild(false)}
              disabled={job.status === 'running'}
              data-testid="podcast-rebuild-btn"
              title="Free - reuses cached narration"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded bg-zinc-800 hover:bg-zinc-700 disabled:opacity-50 border border-zinc-700 text-white text-[10px] font-black uppercase tracking-widest"
            >
              {job.status === 'running' ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
              Re-mix {status?.narrated || 0} narrated
            </button>
            <button
              type="button"
              onClick={() => rebuild(true)}
              disabled={job.status === 'running' || !status?.never_narrated}
              data-testid="podcast-narrate-all-btn"
              title="Spends ElevenLabs credits - narrates stories that have never been voiced"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded bg-red-900/40 hover:bg-red-800/60 disabled:opacity-40 border border-red-700/50 text-red-100 text-[10px] font-black uppercase tracking-widest"
            >
              <Radio className="h-3 w-3" />
              Narrate {status?.never_narrated || 0} new (uses credits)
            </button>
          </div>
        </div>
        {job.status && job.status !== 'idle' && (
          <div className="mt-3 flex flex-wrap items-center gap-3 text-xs" data-testid="podcast-rebuild-status">
            <span className={`px-2 py-0.5 rounded text-[10px] font-black uppercase tracking-[0.2em] ${
              job.status === 'running'
                ? 'bg-yellow-500/15 text-yellow-300 border border-yellow-500/40'
                : 'bg-emerald-500/15 text-emerald-300 border border-emerald-500/40'
            }`}>{job.status}</span>
            <span className="text-zinc-300">{job.done || 0}/{job.total || 0}</span>
            {job.failed > 0 && <span className="text-red-400">failed {job.failed}</span>}
            {job.current && <span className="text-zinc-500 truncate max-w-sm">→ {job.current}</span>}
          </div>
        )}
      </div>
    </div>
  );
};

export default AudioBrandingTab;
